// src/components/PaymentStep.js
import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { getFunctions, httpsCallable, connectFunctionsEmulator } from "firebase/functions";
import { Lock, CreditCard, Check, AlertTriangle } from "lucide-react";

// Initialised once outside any component so Stripe.js loads a single time.
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ""
);

// Connect to the local Functions emulator when running on localhost.
const functions = getFunctions();
if (window.location.hostname === "localhost") {
  connectFunctionsEmulator(functions, "127.0.0.1", 5001);
}
const createPaymentIntentFn = httpsCallable(functions, "createPaymentIntent");

// CardElement appearance — matches the app's input styling.
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      fontSize: "15px",
      color: "#0f172a",
      fontFamily:
        "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      fontSmoothing: "antialiased",
      "::placeholder": { color: "#94a3b8" },
    },
    invalid: {
      color: "#e11d48",
      iconColor: "#e11d48",
    },
  },
};

// ─────────────────────────────────────────────
// Inner form — must be rendered inside <Elements>
// ─────────────────────────────────────────────
function PaymentForm({ pricing, formData, onPaymentSuccess, onBack }) {
  const stripe = useStripe();
  const elements = useElements();

  const [paying, setPaying] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const handlePay = async () => {
    if (!stripe || !elements) return;

    setPaying(true);
    setPaymentError(null);

    try {
      // 1. Ask the Cloud Function for a PaymentIntent client secret.
      const { data } = await createPaymentIntentFn({
        amount: pricing.amount,
        currency: "eur",
        tier: pricing.tier,
        problemSummary: (formData.problem || "").slice(0, 200),
      });

      const clientSecret = data?.clientSecret;
      if (!clientSecret) {
        throw new Error("Could not initialise payment. Please try again.");
      }

      // 2. Confirm card payment with Stripe.js.
      const cardElement = elements.getElement(CardElement);
      const { paymentIntent, error: stripeError } =
        await stripe.confirmCardPayment(clientSecret, {
          payment_method: { card: cardElement },
        });

      if (stripeError) {
        // Stripe's built-in error messages are user-friendly.
        setPaymentError(stripeError.message);
        return;
      }

      if (paymentIntent?.status === "succeeded") {
        // 3. Payment confirmed — create the Firestore problem document.
        //    onPaymentSuccess is async; any failure throws and is caught below.
        await onPaymentSuccess({
          paymentIntentId: paymentIntent.id,
          tier: pricing.tier,
          amount: pricing.amount,
        });
        // After this point the parent sets submitted=true and unmounts us.
      }
    } catch (err) {
      setPaymentError(
        err?.message ||
          "An unexpected error occurred. If your card was charged, contact support@skillsyncs.com."
      );
    } finally {
      setPaying(false);
    }
  };

  // Tier badge colour — mirrors the platform's severity palette (rose = most
  // urgent, amber = elevated, blue = baseline/standard).
  const tierBadge = {
    emergency: "bg-rose-50 text-rose-700 border border-rose-200",
    complex:   "bg-amber-50 text-amber-700 border border-amber-200",
    standard:  "bg-blue-50 text-blue-700 border border-blue-200",
  }[pricing.tier] || "bg-slate-100 text-slate-700";

  return (
    <div className="space-y-6 animate-fadeIn">
      <h3 className="text-lg font-semibold text-slate-900 mb-4">
        Complete Payment to Submit Incident
      </h3>

      {/* Session summary card */}
      <div className="bg-white rounded-lg p-6 border border-slate-200">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <span className={`inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full mb-2 ${tierBadge}`}>
              {pricing.tier.charAt(0).toUpperCase() + pricing.tier.slice(1)}
            </span>
            <p className="text-2xl font-bold text-slate-900">
              {pricing.label}
            </p>
            <p className="text-sm text-slate-500 mt-0.5">
              {pricing.description}
            </p>
          </div>
          <div className="flex-shrink-0 h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-blue-600" />
          </div>
        </div>

        <div className="pt-4 border-t border-slate-200 space-y-2">
          {[
            "Vetted expert on screen within 2 hours (Europe, Mon–Sat 07:00–23:00 CET)",
            "60-minute live working session",
            "Fixed or fully refunded within scope",
          ].map((point) => (
            <div key={point} className="flex items-start gap-2">
              <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-slate-700">{point}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Card input */}
      <div>
        <label className="block text-sm font-medium mb-2 text-slate-700">
          Card details
        </label>
        <div className="border border-slate-300 rounded-lg px-4 py-3 bg-white focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500 transition">
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </div>
      </div>

      {/* Inline payment error — NOT a toast */}
      {paymentError && (
        <div className="border-l-4 border-rose-500 bg-rose-50 rounded-lg p-4 animate-fadeIn">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-rose-700">{paymentError}</p>
          </div>
        </div>
      )}

      {/* Pay button */}
      <div className="flex flex-col items-stretch gap-3 pt-2">
        <button
          type="button"
          onClick={handlePay}
          disabled={paying || !stripe}
          className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition font-medium text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {paying ? (
            <>
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white flex-shrink-0" />
              <span>Processing…</span>
            </>
          ) : (
            <>
              <Lock className="w-4 h-4 flex-shrink-0" />
              <span>
                Pay{" "}
                {pricing.label.split("—")[1]?.trim() || ""} and submit incident
              </span>
            </>
          )}
        </button>

        {/* "Secured by Stripe" trust line */}
        <div className="flex items-center justify-center gap-2">
          <Lock className="w-3.5 h-3.5 text-slate-400" />
          <p className="text-xs text-slate-500">
            Secured by Stripe — your card details never touch our servers
          </p>
        </div>

        {/* Back button */}
        <button
          type="button"
          onClick={onBack}
          disabled={paying}
          className="border border-slate-300 text-slate-700 px-6 py-2.5 rounded-lg hover:bg-slate-50 transition font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
        >
          ← Back to Review
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// Public wrapper — provides the Stripe context
// ─────────────────────────────────────────────
function PaymentStep({ pricing, formData, onPaymentSuccess, onBack }) {
  return (
    <Elements stripe={stripePromise}>
      <PaymentForm
        pricing={pricing}
        formData={formData}
        onPaymentSuccess={onPaymentSuccess}
        onBack={onBack}
      />
    </Elements>
  );
}

export default PaymentStep;
